/**
 * RBAC Types (Reworked - Platform vs Workspace Separation)
 * 
 * TypeScript types for Role-Based Access Control
 */

// ==================== PLATFORM ROLES ====================

export type PlatformRole = 'PLATFORM_USER' | 'PLATFORM_ADMIN';

export const PLATFORM_ROLE_DISPLAY_NAMES: Record<PlatformRole, string> = {
  PLATFORM_USER: 'Platform User',
  PLATFORM_ADMIN: 'Platform Admin',
};

export const PLATFORM_ROLE_DESCRIPTIONS: Record<PlatformRole, string> = {
  PLATFORM_USER: 'Default role for all users. Can create and join workspaces.',
  PLATFORM_ADMIN: 'Internal only. Manages platform infrastructure, billing, and compliance.',
};

// ==================== WORKSPACE ROLES ====================

export type WorkspaceRole = 
  | 'OWNER'
  | 'ORG_ADMIN'
  | 'MANAGER'
  | 'REVIEWER'
  | 'VIEWER'
  | 'GUEST';

// Workspace role hierarchy levels
export const WORKSPACE_ROLE_HIERARCHY: Record<WorkspaceRole, number> = {
  OWNER: 100,
  ORG_ADMIN: 80,
  MANAGER: 40,
  REVIEWER: 20,
  VIEWER: 10,
  GUEST: 0,
};

// Workspace role display names
export const WORKSPACE_ROLE_DISPLAY_NAMES: Record<WorkspaceRole, string> = {
  OWNER: 'Owner',
  ORG_ADMIN: 'Organization Admin',
  MANAGER: 'Manager',
  REVIEWER: 'Reviewer',
  VIEWER: 'Viewer',
  GUEST: 'Guest',
};

// Workspace role descriptions
export const WORKSPACE_ROLE_DESCRIPTIONS: Record<WorkspaceRole, string> = {
  OWNER: 'Full control over workspace. Can manage settings, members, and delete workspace.',
  ORG_ADMIN: 'Can invite/remove users, change roles (except Owner), and manage all items.',
  MANAGER: 'Can create, edit, and organize items. View dashboards and analytics.',
  REVIEWER: 'Can view items, add comments, and view audit trail.',
  VIEWER: 'Read-only access to items and dashboards.',
  GUEST: 'Limited, temporary access with restricted views.',
};

// Legacy aliases for backward compatibility
export type Role = WorkspaceRole;
export const ROLE_HIERARCHY = WORKSPACE_ROLE_HIERARCHY;
export const ROLE_DISPLAY_NAMES = WORKSPACE_ROLE_DISPLAY_NAMES;
export const ROLE_DESCRIPTIONS = WORKSPACE_ROLE_DESCRIPTIONS;

// ==================== WORKSPACE TYPES ====================

export type WorkspaceType = 'PERSONAL' | 'ORGANIZATION';

export const WORKSPACE_TYPE_DISPLAY_NAMES: Record<WorkspaceType, string> = {
  PERSONAL: 'Personal Workspace',
  ORGANIZATION: 'Organization Workspace',
};

export const WORKSPACE_TYPE_DESCRIPTIONS: Record<WorkspaceType, string> = {
  PERSONAL: 'Single-owner workspace for personal use.',
  ORGANIZATION: 'Multi-member workspace with role management and invitations.',
};

// ==================== PERMISSIONS ====================

// Permission enum matching backend
export enum Permission {
  // Platform Management (Platform Admin only - internal)
  PLATFORM_ACCESS_ALL_WORKSPACES = 'platform:access_all_workspaces',
  PLATFORM_MANAGE_WORKSPACES = 'platform:manage_workspaces',
  PLATFORM_GLOBAL_SETTINGS = 'platform:global_settings',
  PLATFORM_SYSTEM_ANALYTICS = 'platform:system_analytics',
  PLATFORM_BILLING = 'platform:billing',
  PLATFORM_COMPLIANCE = 'platform:compliance',
  
  // Workspace Management
  WORKSPACE_VIEW = 'workspace:view',
  WORKSPACE_EDIT = 'workspace:edit',
  WORKSPACE_DELETE = 'workspace:delete',
  WORKSPACE_MANAGE_BILLING = 'workspace:manage_billing',
  WORKSPACE_VIEW_ANALYTICS = 'workspace:view_analytics',
  WORKSPACE_EXPORT_DATA = 'workspace:export_data',
  WORKSPACE_CONVERT_TYPE = 'workspace:convert_type',
  
  // Member Management
  MEMBER_INVITE = 'member:invite',
  MEMBER_REMOVE = 'member:remove',
  MEMBER_VIEW_ALL = 'member:view_all',
  MEMBER_EDIT_ROLES = 'member:edit_roles',
  MEMBER_VIEW_ACTIVITY = 'member:view_activity',
  
  // Team Management
  TEAM_CREATE = 'team:create',
  TEAM_EDIT = 'team:edit',
  TEAM_DELETE = 'team:delete',
  TEAM_MANAGE_MEMBERS = 'team:manage_members',
  TEAM_VIEW_ANALYTICS = 'team:view_analytics',
  
  // Item Management
  ITEM_CREATE = 'item:create',
  ITEM_VIEW_OWN = 'item:view_own',
  ITEM_VIEW_WORKSPACE = 'item:view_workspace',
  ITEM_VIEW_ALL = 'item:view_all',
  ITEM_EDIT_OWN = 'item:edit_own',
  ITEM_EDIT_WORKSPACE = 'item:edit_workspace',
  ITEM_EDIT_ALL = 'item:edit_all',
  ITEM_DELETE_OWN = 'item:delete_own',
  ITEM_DELETE_ALL = 'item:delete_all',
  ITEM_LOCK = 'item:lock',
  ITEM_SUGGEST_EDIT = 'item:suggest_edit',
  ITEM_APPROVE_SUGGESTIONS = 'item:approve_suggestions',
  ITEM_BULK_OPERATIONS = 'item:bulk_operations',
  
  // Category Management
  CATEGORY_CREATE = 'category:create',
  CATEGORY_EDIT = 'category:edit',
  CATEGORY_DELETE = 'category:delete',
  
  // File/Attachment Management
  FILE_UPLOAD = 'file:upload',
  FILE_DOWNLOAD = 'file:download',
  FILE_DELETE_OWN = 'file:delete_own',
  FILE_DELETE_ANY = 'file:delete_any',
  
  // Comments
  COMMENT_CREATE = 'comment:create',
  COMMENT_EDIT_OWN = 'comment:edit_own',
  COMMENT_DELETE_OWN = 'comment:delete_own',
  COMMENT_DELETE_ANY = 'comment:delete_any',
  COMMENT_MODERATE = 'comment:moderate',
  
  // AI Features
  AI_USE_CATEGORIZATION = 'ai:use_categorization',
  AI_USE_VALUATION = 'ai:use_valuation',
  AI_USE_DUPLICATES = 'ai:use_duplicates',
  AI_CONFIGURE = 'ai:configure',
  
  // Audit Trail
  AUDIT_VIEW_OWN = 'audit:view_own',
  AUDIT_VIEW_WORKSPACE = 'audit:view_workspace',
  AUDIT_VIEW_ALL = 'audit:view_all',
  AUDIT_EXPORT = 'audit:export',
  
  // Sharing
  SHARE_CREATE = 'share:create',
  SHARE_MANAGE = 'share:manage',
  
  // Settings
  SETTINGS_VIEW = 'settings:view',
  SETTINGS_EDIT = 'settings:edit',
  SETTINGS_ADMIN = 'settings:admin',
}

// ==================== USER CONTEXT ====================

// User permissions from backend
export interface UserPermissions {
  userId: string;
  platformRole: PlatformRole;
  workspaceId?: string;
  workspaceRole?: WorkspaceRole;
  permissions: Permission[];
  isPlatformAdmin: boolean;
  teamIds?: string[];
}

// Workspace
export interface Workspace {
  id: string;
  name: string;
  description?: string;
  type: WorkspaceType;
  logo?: string;
  role: WorkspaceRole;
  joinedAt?: string;
  createdAt?: string;
  _count?: {
    members: number;
    items: number;
    categories: number;
  };
}

// Workspace member
export interface WorkspaceMember {
  id: string;
  userId: string;
  workspaceId: string;
  role: WorkspaceRole;
  joinedAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    profilePicture?: string;
    lastLogin?: string;
  };
}

// Workspace invitation
export interface WorkspaceInvitation {
  id: string;
  email: string;
  workspaceId: string;
  role: WorkspaceRole;
  token: string;
  invitedById: string;
  invitedBy?: {
    firstName?: string;
    lastName?: string;
    email: string;
  };
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

// Legacy aliases for backward compatibility
export interface OrganizationMember extends WorkspaceMember {
  organizationId: string;
}

// Team (now scoped to Workspace)
export interface Team {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    members: number;
    items: number;
  };
}

// Shared link
export interface SharedLink {
  id: string;
  token: string;
  resourceType: string;
  resourceId: string;
  createdBy: string;
  password?: string;
  maxViews?: number;
  currentViews: number;
  expiresAt?: string;
  allowDownload: boolean;
  allowComments: boolean;
  showPrice: boolean;
  showFullImages: boolean;
  watermarkImages: boolean;
  isActive: boolean;
  createdAt: string;
  shareUrl?: string;
  _count?: {
    accessLogs: number;
  };
}

// Edit suggestion
export interface EditSuggestion {
  id: string;
  itemId: string;
  suggestedBy: string;
  fieldName: string;
  currentValue?: string;
  suggestedValue?: string;
  explanation?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
  createdAt: string;
}

// Temporary elevation
export interface TemporaryElevation {
  id: string;
  userId: string;
  workspaceId: string;
  originalRole: WorkspaceRole;
  elevatedRole: WorkspaceRole;
  scope?: string;
  reason: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string;
  revokedAt?: string;
  revokedBy?: string;
  isActive: boolean;
}
